import { Dispatch, SetStateAction, useCallback, useRef } from 'react';

import type {
  Envelope,
  TokenPayload,
  ToolCallStartPayload,
  ToolCallEndPayload,
  TranscriptPayload,
  EffectFrame,
} from './ws/protocol';
import { emitEffect } from './effects/emitter';
import type { CopilotEffect } from './effects/types';
import type { CopilotArtifact, CopilotMessage } from './CopilotContext';
import {
  addToolCall,
  appendToken,
  attachArtifact,
  finalizeStreaming,
  markInterrupted,
  newId,
  settleLastToolCall,
} from './messageState';

const ANALYSIS_ARTIFACT_KINDS: Record<string, CopilotArtifact['kind']> = {
  SHOW_MEASUREMENT_PLOT: 'plot',
  SHOW_COMPARISON_TABLE: 'comparison',
  REPORT_QUALITY_ISSUES: 'quality',
};

function dispatchEffectFrame(frame: EffectFrame): void {
  emitEffect({ type: frame.type, payload: frame.payload } as CopilotEffect);
}

interface UseMessageHandlerDeps {
  setMessages: Dispatch<SetStateAction<CopilotMessage[]>>;
  setAwaitingReply: Dispatch<SetStateAction<boolean>>;
  setInterimTranscript: Dispatch<SetStateAction<string>>;
  setPendingTranscript: Dispatch<SetStateAction<string | null>>;
}

export function useMessageHandler({
  setMessages,
  setAwaitingReply,
  setInterimTranscript,
  setPendingTranscript,
}: UseMessageHandlerDeps) {
  const streamingIdRef = useRef<string | null>(null);

  const handleMessage = useCallback((env: Envelope) => {
    const ensureStreamId = (): string => {
      if (!streamingIdRef.current) streamingIdRef.current = newId();
      return streamingIdRef.current;
    };

    const routeEffect = (frame: EffectFrame) => {
      const kind = ANALYSIS_ARTIFACT_KINDS[frame.type];
      if (!kind) {
        dispatchEffectFrame(frame);
        return;
      }
      const id = ensureStreamId();
      setMessages((prev) =>
        attachArtifact(prev, id, {
          kind,
          data: frame.payload,
        } as CopilotArtifact)
      );
    };

    switch (env.type) {
      case 'thinking': {
        setAwaitingReply(true);
        break;
      }
      case 'token': {
        setAwaitingReply(false);
        const { text } = env.payload as TokenPayload;
        const id = ensureStreamId();
        setMessages((prev) => appendToken(prev, id, text));
        break;
      }
      case 'tool_call_start': {
        setAwaitingReply(false);
        const { tool_id: toolId, name } = env.payload as ToolCallStartPayload;
        const id = ensureStreamId();
        setMessages((prev) => addToolCall(prev, id, toolId, name));
        break;
      }
      case 'tool_call_end': {
        const { effects } = env.payload as ToolCallEndPayload;
        setMessages((prev) => settleLastToolCall(prev, streamingIdRef.current));
        (effects ?? []).forEach(routeEffect);
        break;
      }
      case 'effect': {
        routeEffect(env.payload as EffectFrame);
        break;
      }
      case 'done': {
        setAwaitingReply(false);
        setMessages(finalizeStreaming);
        streamingIdRef.current = null;
        break;
      }
      case 'error': {
        setAwaitingReply(false);
        setMessages((prev) => markInterrupted(prev, streamingIdRef.current));
        streamingIdRef.current = null;
        break;
      }
      case 'transcript': {
        const { text, final } = env.payload as TranscriptPayload;
        if (final) {
          setInterimTranscript('');
          if (text && text.trim()) setPendingTranscript(text.trim());
        } else {
          setInterimTranscript(text);
        }
        break;
      }
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { handleMessage, streamingIdRef };
}
